import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function renderSectionBody(bodyText: string) {
  const lines = bodyText.split('\n').filter(line => line.trim() !== '');
  return lines.map((line, i) => {
    const bulletMatch = line.match(/^[•\d.]+/);
    if (line.startsWith('•') || (bulletMatch && /^\d+\.\d+/.test(bulletMatch[0]))) {
      const content = line.replace(/^(?:•\s*|\d+\.\d+\s*)/, '');
      return (
        <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--accent)', marginTop: 2, flexShrink: 0 }}>{line.startsWith('•') ? '•' : bulletMatch?.[0]}</span>
          <span>{content}</span>
        </li>
      );
    }
    return <p key={i} style={i === 0 ? undefined : { marginTop: 16 }}>{line}</p>;
  });
}

export default function TermsPage() {
  const { t } = useTranslation("home");
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--page-bg)",
        display: "flex",
        flexDirection: "column",
        fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
        color: "var(--text-secondary)",
      }}
    >
      <style>{`
        .tm-header {
          background-color: var(--page-bg);
          border-bottom: 1px solid var(--border);
          position: relative;
        }
        .tm-header-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .tm-back {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: var(--text-secondary);
          text-decoration: none;
          white-space: nowrap;
          cursor: pointer;
        }
        .tm-back:hover { color: var(--text-primary); }
        .tm-title-wrap {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .tm-title {
          font-size: 18px;
          font-weight: 700;
          color: var(--text-primary);
          margin: 0;
        }
        .tm-subtitle {
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 2px;
        }
        .tm-placeholder { width: 80px; }

        .tm-main {
          flex: 1;
          max-width: 768px;
          width: 100%;
          margin: 0 auto;
          padding: 40px 24px 64px;
        }
        @media (min-width: 640px) {
          .tm-main { padding: 64px 40px 80px; }
          .tm-title { font-size: 20px; }
        }

        .tm-banner {
          position: relative;
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 40px;
        }
        .tm-banner img {
          width: 100%;
          height: 200px;
          object-fit: cover;
          opacity: 0.5;
          display: block;
        }
        .tm-banner-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(31,16,48,1), rgba(31,16,48,0.6), rgba(31,16,48,0));
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 0 24px;
        }
        .tm-banner-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: rgba(201,169,110,0.1);
          border: 1px solid rgba(201,169,110,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 12px;
        }
        .tm-banner-icon i { font-size: 24px; color: #c9a96e; }
        .tm-banner-title {
          font-size: 18px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
        }
        .tm-banner-desc {
          font-size: 14px;
          color: #c9c9d5;
          margin: 0;
        }

        .tm-badges {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 40px;
        }
        @media (min-width: 640px) { .tm-badges { gap: 16px; } }
        .tm-badge {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          border-radius: 16px;
          border: 1px solid var(--border);
          background: var(--card-bg);
          padding: 16px 20px;
        }
        .tm-badge-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tm-badge-icon i { font-size: 20px; color: var(--accent); }
        .tm-badge-label {
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
        }

        .tm-card {
          border-radius: 16px;
          border: 1px solid var(--border);
          background: var(--card-bg);
          padding: 24px;
          margin-bottom: 24px;
        }
        .tm-card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
        }
        .tm-card-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tm-card-icon i { font-size: 16px; color: var(--accent); }
        .tm-card-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }
        .tm-card-body {
          font-size: 14px;
          line-height: 1.75;
          color: var(--text-secondary);
        }
        .tm-card-body p { margin: 0 0 12px; }
        .tm-card-body ul {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .tm-card-body li {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
        .tm-bullet {
          color: var(--accent);
          margin-top: 2px;
          flex-shrink: 0;
        }

        .tm-contact {
          text-align: center;
        }
        .tm-contact-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: var(--accent-soft);
          border: 1px solid var(--accent-border);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
        }
        .tm-contact-icon i { font-size: 24px; color: var(--accent); }
        .tm-contact-title {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 4px;
        }
        .tm-contact-desc {
          font-size: 14px;
          color: var(--text-secondary);
          margin: 0 0 16px;
        }
        .tm-contact-email {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          color: var(--accent-hover);
          text-decoration: none;
        }
        .tm-contact-email:hover { color: var(--accent); }
      `}</style>

      {/* Header */}
      <header className="tm-header">
        <div className="tm-header-inner">
          <Link to="/" className="tm-back">
            <i className="ri-arrow-left-s-line" style={{ fontSize: 18 }} />
            <span>{t("terms.back_home")}</span>
          </Link>
          <div className="tm-title-wrap">
            <h1 className="tm-title">{t("terms.title")}</h1>
            <span className="tm-subtitle">{t("terms.subtitle")}</span>
          </div>
          <div className="tm-placeholder" />
        </div>
      </header>

      <main className="tm-main">
        {/* Banner */}
        <div className="tm-banner">
          <img
            src="/images/misc/8b73ccc902af6364eef235bd7569080b.jpg"
            alt={t("terms.banner_alt")}
          />
          <div className="tm-banner-overlay">
            <div className="tm-banner-icon">
              <i className="ri-file-text-line" />
            </div>
            <h2 className="tm-banner-title">{t("terms.banner_title")}</h2>
            <p className="tm-banner-desc">{t("terms.banner_desc")}</p>
          </div>
        </div>

        {/* Badges */}
        <div className="tm-badges">
          <div className="tm-badge">
            <div className="tm-badge-icon">
              <i className="ri-lock-2-line" />
            </div>
            <span className="tm-badge-label">{t("terms.badge1")}</span>
          </div>
          <div className="tm-badge">
            <div className="tm-badge-icon">
              <i className="ri-compass-3-line" />
            </div>
            <span className="tm-badge-label">{t("terms.badge2")}</span>
          </div>
          <div className="tm-badge">
            <div className="tm-badge-icon">
              <i className="ri-lightbulb-line" />
            </div>
            <span className="tm-badge-label">{t("terms.badge3")}</span>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="tm-card">
          <div className="tm-card-header">
            <i className="ri-error-warning-line" style={{ fontSize: 16, color: "var(--accent)" }} />
            <h3 className="tm-card-title">{t("terms.disclaimer_title")}</h3>
          </div>
          <div className="tm-card-body">
            <p>
              {t("terms.disclaimer_p1")}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "var(--accent-soft)",
                    border: "1px solid var(--accent-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <i className="ri-shield-check-line" style={{ fontSize: 14, color: "var(--accent)" }} />
                </div>
                <span style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)" }}>
                  {t("terms.disclaimer_b1")}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "var(--accent-soft)",
                    border: "1px solid var(--accent-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <i className="ri-compass-3-line" style={{ fontSize: 14, color: "var(--accent)" }} />
                </div>
                <span style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)" }}>
                  {t("terms.disclaimer_b2")}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: "var(--accent-soft)",
                    border: "1px solid var(--accent-border)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                >
                  <i className="ri-lightbulb-line" style={{ fontSize: 14, color: "var(--accent)" }} />
                </div>
                <span style={{ fontSize: 14, lineHeight: 1.75, color: "var(--text-secondary)" }}>
                  {t("terms.disclaimer_b3")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 一、服务说明 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-apps-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec1.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec1.body"))}
            </ul>
          </div>
        </div>

        {/* 二、用户账号 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-user-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec2.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec2.body"))}
            </ul>
          </div>
        </div>

        {/* 三、付费服务与退款政策 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-vip-crown-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec3.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec3.body"))}
            </ul>
          </div>
        </div>

        {/* 四、用户行为规范 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-user-settings-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec4.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec4.body"))}
            </ul>
          </div>
        </div>

        {/* 五、命理服务免责声明 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-shield-check-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec5.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec5.body"))}
            </ul>
          </div>
        </div>

        {/* 六、知识产权 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-copyright-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec6.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec6.body"))}
            </ul>
          </div>
        </div>

        {/* 七、服务变更与终止 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-refresh-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec7.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec7.body"))}
            </ul>
          </div>
        </div>

        {/* 八、条款修改 */}
        <div className="tm-card">
          <div className="tm-card-header">
            <div className="tm-card-icon">
              <i className="ri-edit-line" />
            </div>
            <h3 className="tm-card-title">{t("terms.sec8.title")}</h3>
          </div>
          <div className="tm-card-body">
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderSectionBody(t("terms.sec8.body"))}
            </ul>
          </div>
        </div>

        {/* Contact */}
        <div className="tm-card tm-contact">
          <div className="tm-contact-icon">
            <i className="ri-question-answer-line" />
          </div>
          <p className="tm-contact-title">{t("terms.contact_title")}</p>
          <a href="mailto:dazhuotech@gmail.com" className="tm-contact-email">
            dazhuotech@gmail.com
          </a>
        </div>
      </main>
    </div>
  );
}